import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Layout } from './components/Layout';
import { SignupForms } from './pages/SignupForms';
import { SignupFormEditor } from './pages/SignupFormEditor';
import { EmbedCodes } from './pages/EmbedCodes';
import { EmailTemplates } from './pages/EmailTemplates';
import { EmailTemplateEditor } from './pages/EmailTemplateEditor';
import { FormConfiguration } from './pages/FormConfiguration';
import { ConfigurationsList } from './pages/ConfigurationsList';
import { Submissions } from './pages/Submissions';
import { Subscriptions } from './pages/Subscriptions';
import { Contacts } from './pages/Contacts';
import { Users } from './pages/Users';
import { Settings } from './pages/Settings';
import { AccountSettings } from './pages/AccountSettings';
import { SuperAdminSettings } from './pages/SuperAdminSettings';
import { Newsletters } from './pages/Newsletters';
import { NewsletterEditor } from './pages/NewsletterEditor';
import { NewsletterDetail } from './pages/NewsletterDetail';
import { Campaigns } from './pages/Campaigns';
import { CampaignBuilder } from './pages/CampaignBuilder';
import { CampaignDetail } from './pages/CampaignDetail';
import { AudienceBuilder } from './pages/AudienceBuilder';
import { AudienceEditor } from './pages/AudienceEditor';
import { AuditLog } from './pages/AuditLog';
import { Tags } from './pages/Tags';
import { TagDetail } from './pages/TagDetail';
import { Categories } from './pages/Categories';
import { Regions } from './pages/Regions';
import { MailingLists } from './pages/MailingLists';
import { MailingListDetail } from './pages/MailingListDetail';
import { RolesPermissions } from './pages/RolesPermissions';
import { WorkspaceProvider, useWorkspace } from './contexts/WorkspaceContext';
import { SendBucketManager } from './pages/SendBucketManager';
import { ApiTest } from './pages/ApiTest';
import { GptApiTest } from './pages/GptApiTest';
import { ErrorLog } from './pages/ErrorLog';
import { ConfirmSubscription } from './pages/ConfirmSubscription';
import { Onboarding } from './pages/Onboarding';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { FormTemplatesLibrary } from './pages/FormTemplatesLibrary';
import { FormTemplateLibraryEditor } from './pages/FormTemplateLibraryEditor';
import { EmailTemplatesLibrary } from './pages/EmailTemplatesLibrary';
import { EmailTemplateLibraryEditor } from './pages/EmailTemplateLibraryEditor';
import { BrazilContentReference } from './pages/BrazilContentReference';
import { LandingPages } from './pages/LandingPages';
import { LandingPageEditor } from './pages/LandingPageEditor';
import { PublicLandingPage } from './pages/PublicLandingPage';
import { PublicFormEmbed } from './pages/PublicFormEmbed';
import { MediaLibrary } from './pages/MediaLibrary';
function AppGate() {
  const {
    appState,
    error
  } = useWorkspace();
  // Loading state
  if (appState === 'loading') {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-0" data-mp="1d47ydkh1r92a">
        <div className="text-center" data-id="element-1" data-mp="1d6atgix23sdv">
          <Loader2 className="h-10 w-10 text-blue-500 animate-spin mx-auto mb-4" data-id="element-2" data-mp="ygd5a2z4n2oo" />
          <p className="text-sm text-gray-500 font-medium" data-id="element-3" data-mp="13e54li23pr2w">
            Loading workspace...
          </p>
        </div>
      </div>;
  }
  // Error state
  if (appState === 'error') {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-4" data-mp="1w3vk9vjdt114">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10 text-center max-w-md w-full" data-id="element-5" data-mp="1i3ukykirm1nr">
          <div className="mx-auto w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-6" data-id="element-6" data-mp="1m4mt1afwi0ig">
            <AlertTriangle className="h-8 w-8 text-red-500" data-id="element-7" data-mp="6o5w1nlyaskb" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-8" data-mp="apr856qnv2tg">
            Connection Error
          </h1>
          <p className="text-gray-500 text-sm mb-2" data-id="element-9" data-mp="qxnnhq2semx6">
            Failed to connect to the backend. Please check your connection and
            try again.
          </p>
          {error && <p className="text-xs text-red-500 bg-red-50 rounded-lg p-3 mb-6 font-mono break-all" data-id="element-10" data-mp="h6sy4cu8v77i">
              {error}
            </p>}
          <button onClick={() => window.location.reload()} className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors" data-id="element-11" data-mp="ca2jlty38jo7">
            <RefreshCw className="h-4 w-4" data-id="element-12" data-mp="zxk3l3b42dgm" />
            Retry
          </button>
        </div>
      </div>;
  }
  // Onboarding — no users exist yet
  if (appState === 'onboarding') {
    return <Onboarding data-id="element-13" data-mp="3ko2qqlqf8wtk" />;
  }
  // Login — users exist but no session
  if (appState === 'login') {
    return <Login data-id="element-14" data-mp="3gmh6ugfgzi9v" />;
  }
  // Ready — full admin app
  return <Layout data-id="element-15" data-mp="3gkwcaqt5nzc0">
      <Routes data-id="element-16" data-mp="3gl650m5l4kle">
        <Route path="/" element={<Navigate to="/dashboard" replace data-id="element-18" data-mp="1w4ougee9jouc" />} data-id="element-17" data-mp="3gotzpomrnd1p" />
        <Route path="/dashboard" element={<Dashboard data-id="element-20" data-mp="2060aptb8yxpv" />} data-id="element-19" data-mp="1w5zy8e7pkwlg" />

        <Route path="/mailing-lists" element={<MailingLists data-id="element-22" data-mp="206jrv7t2pi2u" />} data-id="element-21" data-mp="205iid7rph3n8" />
        <Route path="/mailing-lists/:listId" element={<MailingListDetail data-id="element-24" data-mp="25425hbi1clh6" />} data-id="element-23" data-mp="203ntxjh86tee" />

        <Route path="/newsletters" element={<Newsletters data-id="element-26" data-mp="29vjur2ap4xmb" />} data-id="element-25" data-mp="253sf3mt2wcay" />
        <Route path="/newsletters/new" element={<NewsletterEditor data-id="element-28" data-mp="29x4st1w7yyox" />} data-id="element-27" data-mp="29w3gyv7dq865" />
        <Route path="/newsletters/:id" element={<NewsletterDetail data-id="element-30" data-mp="2eu4uz1py7har" />} data-id="element-29" data-mp="2ev6ja7vz60af" />
        <Route path="/newsletters/:id/edit" element={<NewsletterEditor data-id="element-32" data-mp="2jt82cstehzlw" />} data-id="element-31" data-mp="2ex0swq1sq5z7" />

        <Route path="/contacts" element={<Contacts data-id="element-34" data-mp="2jvuaatk7eh4n" />} data-id="element-33" data-mp="2jsg75j3pu3w5" />

        <Route path="/forms" element={<SignupForms data-id="element-36" data-mp="2nwwb3etrr18q" />} data-id="element-35" data-mp="2nv339w7f6az5" />
        <Route path="/forms/new" element={<SignupFormEditor data-id="element-38" data-mp="2sufmm6z30zwz" />} data-id="element-37" data-mp="2nti7k39dclzp" />
        <Route path="/forms/:id" element={<SignupFormEditor data-id="element-40" data-mp="2sslczot9gu87" />} data-id="element-39" data-mp="2stmmhoump8nt" />
        <Route path="/forms/:id/embed" element={<EmbedCodes data-id="element-42" data-mp="tivb2qmhg872" />} data-id="element-41" data-mp="thtpvtbr65bm" />

        <Route path="/templates" element={<EmailTemplates data-id="element-44" data-mp="ygeo5n7g2exf" />} data-id="element-43" data-mp="tjocdc2oa5ht" />
        <Route path="/templates/new" element={<EmailTemplateEditor data-id="element-46" data-mp="yir7o4suoxcl" />} data-id="element-45" data-mp="yhpwm0bu4auv" />
        <Route path="/templates/:id" element={<EmailTemplateEditor data-id="element-48" data-mp="21s3zb9dw10p9" />} data-id="element-47" data-mp="21r2a8101edoj" />

        <Route path="/form-templates" element={<FormTemplatesLibrary data-id="element-50" data-mp="1wt98pl9oi7ul" />} data-id="element-49" data-mp="21tf0r2jv1wd7" />
        <Route path="/form-templates/new" element={<FormTemplateLibraryEditor data-id="element-52" data-mp="2asik87mxwds8" />} data-id="element-51" data-mp="1wubwhkbpfu6a" />
        <Route path="/form-templates/:id" element={<FormTemplateLibraryEditor data-id="element-54" data-mp="25s4g30oglnzh" />} data-id="element-53" data-mp="2aq5vnbmof567" />

        <Route path="/email-templates-library" element={<EmailTemplatesLibrary data-id="element-56" data-mp="2kovbkyv0ifjt" />} data-id="element-55" data-mp="2kqg9mygjcgmf" />
        <Route path="/email-templates-library/new" element={<EmailTemplateLibraryEditor data-id="element-58" data-mp="2fp90ow2npbe6" />} data-id="element-57" data-mp="2frlf4z97h3mh" />
        <Route path="/email-templates-library/:id" element={<EmailTemplateLibraryEditor data-id="element-60" data-mp="2ph53uljlnrp9" />} data-id="element-59" data-mp="2tj019eyetkxr" />

        <Route path="/configurations" element={<ConfigurationsList data-id="element-62" data-mp="33gxn5vssr5n7" />} data-id="element-61" data-mp="2phojtwpow60n" />
        <Route path="/configuration/new" element={<FormConfiguration data-id="element-64" data-mp="33h7ebmpkviuh" />} data-id="element-63" data-mp="33g4qxorgryjb" />
        <Route path="/configuration/:id" element={<FormConfiguration data-id="element-66" data-mp="2yj5v91s5jjj0" />} data-id="element-65" data-mp="2yi36azee3r5q" />

        <Route path="/tags" element={<Tags data-id="element-68" data-mp="3i3we7pnqry1e" />} data-id="element-67" data-mp="2yfqi44i1gkk8" />
        <Route path="/tags/:tagName" element={<TagDetail data-id="element-70" data-mp="3i4ef4zlcn0fn" />} data-id="element-69" data-mp="3i3d2iqooq5vt" />

        <Route path="/categories" element={<Categories data-id="element-72" data-mp="3n2zfr04ijm4m" />} data-id="element-71" data-mp="3n2pq5dndrgzg" />

        <Route path="/regions" element={<Regions data-id="element-74" data-mp="3n0d8z2pg5ang" />} data-id="element-73" data-mp="3n27og1hy8ak5" />

        <Route path="/campaigns" element={<Campaigns data-id="element-76" data-mp="384nm72cfstho" />} data-id="element-75" data-mp="385yosyu5bv77" />
        <Route path="/campaigns/new" element={<CampaignBuilder data-id="element-78" data-mp="3d40jkh0xpiyi" />} data-id="element-77" data-mp="387jlau00to7p" />
        <Route path="/campaigns/:id" element={<CampaignDetail data-id="element-80" data-mp="3d6czkon4tj8x" />} data-id="element-79" data-mp="3d38mt2vlpf6n" />

        <Route path="/audiences" element={<AudienceBuilder data-id="element-82" data-mp="2zcdcdm5geq0y" />} data-id="element-81" data-mp="2zclp9e1ktvc5" />
        <Route path="/audiences/new" element={<AudienceEditor data-id="element-84" data-mp="344dixxg14h6i" />} data-id="element-83" data-mp="2zb2ajrvkjsch" />
        <Route path="/audiences/:id" element={<AudienceEditor data-id="element-86" data-mp="345ompx9h5oxm" />} data-id="element-85" data-mp="344n5fb1v867g" />

        <Route path="/submissions" element={<Submissions data-id="element-88" data-mp="2pgip2dqac6ru" />} data-id="element-87" data-mp="2pfh8jtqi2s2q" />
        <Route path="/subscriptions" element={<Subscriptions data-id="element-90" data-mp="2ud92etapgo9k" />} data-id="element-89" data-mp="2pd4j6viexffn" />
        <Route path="/audit-log" element={<AuditLog data-id="element-92" data-mp="2ubpjsw1ku14m" />} data-id="element-91" data-mp="2uek5ss08nu05" />
        <Route path="/users" element={<Users data-id="element-94" data-mp="2gfc7007zmnwh" />} data-id="element-93" data-mp="2gej1t3oog6gg" />
        <Route path="/roles" element={<RolesPermissions data-id="element-96" data-mp="2kfuy1s223pxm" />} data-id="element-95" data-mp="2gf2o0wld1h0a" />
        <Route path="/settings" element={<Settings data-id="element-98" data-mp="2kfcv6ckw2dgq" />} data-id="element-97" data-mp="2kg4f2q54imr6" />
        <Route path="/account" element={<AccountSettings data-id="element-100" data-mp="30vx1lp56bli3" />} data-id="element-99" data-mp="2khp98enj03oi" />

        <Route path="/send-buckets" element={<SendBucketManager data-id="element-102" data-mp="30t139zpeyut4" />} data-id="element-101" data-mp="30u2jeilae7hp" />

        <Route path="/api-test" element={<ApiTest data-id="element-104" data-mp="2vwtxc6x0pjb6" />} data-id="element-103" data-mp="2vvr43sc834s3" />
        <Route path="/api-test/gpt" element={<GptApiTest data-id="element-106" data-mp="2qwobj7dfir10" />} data-id="element-105" data-mp="2vu7ng0mnmrps" />

        <Route path="/error-log" element={<ErrorLog data-id="element-108" data-mp="2qzahj2ko8yh4" />} data-id="element-107" data-mp="2qvwgbxnquvb9" />

        <Route path="/brazil-content" element={<BrazilContentReference data-id="element-110" data-mp="2lxbejtsug3kp" />} data-id="element-109" data-mp="2lyd2gyuykkju" />

        <Route path="/landing-pages" element={<LandingPages data-id="element-112" data-mp="2hx16mblhr337" />} data-id="element-111" data-mp="2m0h4fb9ar9hh" />
        <Route path="/landing-pages/new" element={<LandingPageEditor data-id="element-114" data-mp="2hycaebexsaub" />} data-id="element-113" data-mp="2hxb0wbdkjwep" />
        <Route path="/landing-pages/:id" element={<LandingPageEditor data-id="element-116" data-mp="2d4hxprxr0w4q" />} data-id="element-115" data-mp="2d5j77rz49akc" />

        <Route path="/media-library" element={<MediaLibrary data-id="element-118" data-mp="287reivqndkf3" />} data-id="element-117" data-mp="286ge95weuusq" />

        <Route path="/super-admin" element={<SuperAdminSettings data-id="element-120" data-mp="237vef7l2md4t" />} data-id="element-119" data-mp="286znzzbwqcvo" />
      </Routes>
    </Layout>;
}
export function App() {
  return <WorkspaceProvider data-id="element-121" data-mp="1y8acqpeinqoe">
      <Toaster position="top-right" richColors closeButton data-id="element-122" data-mp="1y7h23dcfsh0z" />
      <Router data-id="element-123" data-mp="1u6x4y9rv5deu">
        <Routes data-id="element-124" data-mp="1u6f1ot6s9yxf">
          {/* Public routes — no auth needed */}
          <Route path="/confirm" element={<ConfirmSubscription data-id="element-126" data-mp="9pfmp1inbind" />} data-id="element-125" data-mp="1u91hth7qpmr9" />
          <Route path="/page/:slug" element={<PublicLandingPage data-id="element-128" data-mp="9r8tcgt9e2vd" />} data-id="element-127" data-mp="9o4ln9gl4ozy" />
          <Route path="/embed/:slug" element={<PublicFormEmbed data-id="element-130" data-mp="dowtpzdq0nun" />} data-id="element-129" data-mp="dpq3l97z7th0" />

          {/* All other routes go through the auth/onboarding gate */}
          <Route path="*" element={<AppGate data-id="element-132" data-mp="inrntxh51yu2" />} data-id="element-131" data-mp="iqdrvn4tlw7j" />
        </Routes>
      </Router>
    </WorkspaceProvider>;
}